import { enqueueSnackbar } from "notistack"
import { ProjectCategory, tagsDetail } from "pages/Common/commonTypes"
import { IProjectProfileOverviewResponse, IProjectTicket } from "pages/Projects/ProjectTypes"
import { BulkGrnSubmitItem } from "shared/BulkGrnModal"
import { api } from "../../../../api"
import { setAlertFunc } from "../../../../components/AlertToaster/AlertSlice"
import {
    HistoryResponse,
    IFile,
    IResponse,
    StatusButton,
    StatusTabsData,
    TicketDataResponseType,
} from "../../../Common/commonTypes"
import { getTicketDetails } from "../../../Projects/ProjectSlice"
import { getDemolition, getDemolitionTabs } from "../../OperationSlice"
import { SurveyContractor } from "../Survey/SurveyService"

const MODULE_URL = "operations"
const PROJECT_URL = "projects"
// ==================== V3 Types ====================

export type InstallationParentStatus =
    | "all_tab"
    | "open"
    | "foundation"
    | "cp_installation"
    | "termination"
    | "commissioning"
    | "partially_completed"
    | "completed"
    | "dropped"
    | "blocked"

export interface IInstallationTabCount {
    status: string
    ticket_count: number
    parent_status_id: InstallationParentStatus
    status_color: string
    status_text_color: string
    child_status_id?: number[]
    sub_counts: {
        all: number
        outright: number
        cpo: number
    } | null
}

export interface IInstallationListFilters {
    page?: number
    per_page?: number
    search_key?: string
    // Quick filters
    status_id?: string
    progress_range?: string | number
    // More filters
    assigned_to?: number
    raised_by_id?: number
    deal_done_by_id?: number
    business_poc_ids?: string
    template_id?: string
    program_ids?: string
    category_ids?: string
    state_id?: string
    city_id?: string
    site_ids?: string
    tag_ids?: string
    charger_type?: string
    vendor_ids?: string
    deal_type?: "Outright" | "Cpo"
    on_hold?: string
    on_hold_reason?: string | number
    read_filter?: string
    raised_on_start?: string
    raised_on_end?: string
    updated_on_start?: string
    updated_on_end?: string
    requested_closure_start?: string
    requested_closure_end?: string
    target_closure_start?: string
    target_closure_end?: string
    age?: string
    age_op?: string
    blocked_reason_id?: string
}

export interface IInstallationSite {
    id: number | null
    name: string | null
    site_id_str: string | null
    address: string | null
    pincode: number | null
    city: string | null
    state: string | null
    tier: string | null
}

export interface IInstallationStatus {
    id: number
    label: string
    color: string | null
    text_color: string | null
    parent_status: string | null
    parent_status_id: number | null
}

export interface IInstallationTag {
    id: number
    name: string
    color: string
    text_color: string
}

export interface IInstallationAccount {
    account_id: number
    account_name: string
    account_type: string
}

export interface IInstallationStatusUpdatePayload {
    ticket_id: number | string
    new_status_key?: string
    reason_id?: number
    page?: 1 | 2
    checklist?: { item_key: string; is_checked: boolean }[]
}

export interface IInstallationListRow {
    ops_ticket_id: number
    display_id: string | null
    project_id: number | null
    age: string
    on_hold_tat: string
    project_name: string | null
    deal_type: string | null
    category: ProjectCategory
    template_name: string | null
    accounts_data: IInstallationAccount[]
    programs: { program_id: number; program_name: string }[]
    site: IInstallationSite
    status: IInstallationStatus
    assigned_to: { id: number | null; name: string | null }
    business_poc: { id: number | null; name: string | null } | null
    raised_by: string | null
    flags: {
        drop_flag: boolean
        is_dropped: boolean
        is_unread: boolean | null
    }
    on_hold: {
        on_hold: boolean
        reason?: string | null
        standby?: boolean | null
        tentative_date?: string | null
    }
    tags: {
        primary: IInstallationTag[]
        others: IInstallationTag[]
    }
    reason: string | null
    reason_type: string | null
    dropped_reason: string | null
    target_completion_date: string | null
    requested_closure_date: string | null
    closed_on: string | null
    completion_progress: number | null
    tat_days: number | null
    chargers: {
        charger_details: { name: string; quantity: number }[]
        total: number
    }
    scope_of_work: string | null
    scope_of_work_statuses: string[] | null
    raised_on: string
    updated_on: string
}

export interface IInstallationListMeta {
    request_id: string
    parent_status: string
    page: number
    per_page: number
    total: number
    total_pages: number
    sort: string
    filters_applied: Record<string, any>
}

export interface IInstallationListResponse {
    meta: IInstallationListMeta
    rows: IInstallationListRow[]
}

type OpsCompletionDateObj = {
    completion_date: string
    timestamp: string
    user_id: number
    username: string
    reason?:string
}

export type CardData = {
    assigned_to: string
    assigned_to_id: number
    display_id: string
    status: string
    status_color_code: string
    text_color_code: string
    ticket_id: number
    ticket_type: string
    survey_report_url?: any
    data?: any
    solo_logistics_tickets: any
    solo_data?: any
    card_name: string
    main_ticket_dr?: string
    progress: number
}

export type DocHubObj = {
    comment_id?: null
    doc_name: string
    doc_type: string
    file_url: IFile
    is_comment: boolean
    remarks: string
    stage: string
    uploaded_on: string
}

export type { ChangeOrderObj } from "pages/Common/commonTypes"

// ==================== Estimate / GRN Types ====================

export type EstimateStatus = "open" | "approved" | "on-hold" | "rejected"
export type GrnStatus = "open" | "grn_done"

export interface IEstimateItem {
    sno: number
    item_id: number
    sku_code: string
    boq_type: "Service" | "Material"
    description: string
    source: "Warehouse" | "Third Party"
    quantity: number
    grn_quantity: number | null
    grn_mismatch: boolean
    price_per_unit: number
    total_cost: number
    remarks: string | null
    grn_status: GrnStatus
    grn_file_url: string | null
}

export interface IFinalEstimateItem extends IEstimateItem {
    scope: "Vendor" | "Statiq" | null
}

export interface IInitialSiteEstimate {
    boq_id: number
    status: EstimateStatus
    status_label: string
    totals: {
        total: number
        service: number
        material: number
    }
    items: IEstimateItem[]
}

export interface IFinalSiteEstimate {
    boq_id: number
    status: EstimateStatus
    totals: {
        grand_total: number
        pct_change: number | null
        direction: "up" | "down" | "same" | null
    }
    items: IFinalEstimateItem[]
}

export interface IEstimateResponse {
    ticket_id: number
    initial_site_estimate: IInitialSiteEstimate
    final_site_estimate: IFinalSiteEstimate
}

export interface IGrnImage {
    doc_id: number
    name: string
    doc_type: string
    is_excel: boolean
    file_name: string
    urls: {
        view: string
        download: string
    }
}

export interface IGrnDetailsResponse {
    grn_status: GrnStatus
    invoice_no: string | null
    invoice_date: string | null
    grn_images: IGrnImage[]
}

export interface IUploadGrnResponse {
    message: string
    grn_images: Omit<IGrnImage, "doc_id">[]
}

export interface IBoqGrnEntry {
    id: number
    grn_qty: number
    grn_date: string
    remarks: string | null
    attachment: {
        doc_id: number | null
        doc_type: string
        file_name: string
        is_excel: boolean
        is_image: boolean
        name: string
        urls: { view: string; download: string }
    } | null
    done_by: { id: number; name: string } | null
    created_at: string
}

export interface IAppiTicket {
    ticket_id: number
    display_id: string
    discom_name: string
    application_date: string
}

export interface IBoqGrnDetailsResponse {
    item_id: number
    boq_id: number
    boq_qty: number
    grn_qty_received: number
    remaining_qty: number
    grn_entries: IBoqGrnEntry[]
}

export interface IAddBoqGrnResponse {
    grn_id: number
    message: string
}

export interface IBulkAddBoqGrnResponse {
    added_count: number
    added_grn_ids: number[]
    errors: { item_id: number; reason: string }[]
}

export interface IChargerDetailedItem {
    branding: string | null
    charger_name: string | null
    charger_sub_status: string | null
    commissioning_date: string | null
    commissioning_report_url: string | null
    display_name: string
    description: string | null
    installation_date: string | null
    installation_sequence: number | null
    insurance: boolean | null
    onboarding_date: string | null
    onboarding_ticket_id: number | null
    onboarding_display_id: string | null
    operational_status: string | null
    ops_power_rating: number | null
    project_product_id: number
    serial_number: string | null
    sku_code: string | null
}

export interface IChargerSkuItem {
    charger_type: string | null
    count: string | number | null
    description: string | null
    sku_code: string | null
}

export interface IChargerDetailsResponse {
    charger_details: Array<IChargerDetailedItem | IChargerSkuItem>
    max_installation_sequence?: number | null
    totals?: {
        commissioned: number
        installed: number
        onboarded: number
        total: number
    }
    view: "detailed" | "sku"
}

/**
 * Parent operational-status groups eligible for onboarding initiation.
 * Passed to the charger_details endpoint as the `operational_status` filter.
 */
export const ONBOARDING_ELIGIBLE_OPERATIONAL_STATUSES = "open,delivered,installed"

export interface IOnboardingPlpSequence {
    plp_id: number
    sequence: number
}

export interface IOnboardingSitePayload {
    name?: string
    country_id?: number
    state_id?: number
    city_id?: number
    pincode?: number
    latitude?: number
    longitude?: number
}

export interface IOnboardingDirectionStepPayload {
    sequence_no: number
    title: string
    description: string
    file?: string
}

export interface IOnboardingDirectionsPayload {
    start_point?: string
    end_point?: string
    steps: IOnboardingDirectionStepPayload[]
}

export interface ICreateOnboardingTicketPayload {
    project_id: number
    assigned_to: number
    plp_sequences?: IOnboardingPlpSequence[]
    site?: IOnboardingSitePayload
    directions?: IOnboardingDirectionsPayload
}

export interface IInstallationStatsResponse {
    age_days: number
    business_poc: {
        id: number | null
        name: string | null
    }
    category: ProjectCategory
    closed_on: string | null
    deal_type: string
    display_id: string
    on_hold: {
        on_hold: boolean
        reason?: string
        standby?: string
        tentative_date?: string
    }
    ops_ticket_id: number
    progress: number | null
    project_display_id: string
    project_id: number
    project_name: string
    site_id: number | null
    site_name: string
    start_date: string | null
    status: {
        color: string
        status: string
        status_id: number
        text_color: string
    }
    tags: {
        others: tagsDetail[]
        primary: tagsDetail[]
    }
    target_completion_date: string | null
    tat_days: number | null
    template_id: number | null
    template_name: string
}

export interface IChargerPresignedUploadUrl {
    file_name: string
    presigned_url: string
    file_url: string
}

export interface IChargerPresignedUploadResponse {
    urls: IChargerPresignedUploadUrl[]
}

export interface IChargerPresignedUploadRequest {
    plp_id: number
    file_names: string[]
}

export interface IBulkUpdateChargerItem {
    plp_id: number
    operational_status: number
    commissioning_date?: string
    go_live_date?: string
    installation_date?: string
    commissioning_report_url?: string
    installation_report_urls?: string[]
    decommissioning_date?: string
}

export interface IBulkUpdateChargerStatusRequest {
    chargers: IBulkUpdateChargerItem[]
}

const installationService = api.injectEndpoints({
    endpoints: build => ({
        getInstallationTabs: build.query({
            query: params => ({
                url: `${MODULE_URL}/ticket/total`,
                params: {
                    state_id: JSON.stringify(params.data.stateId ?? []),
                    status_id: JSON.stringify(params.data.statusId ?? []),
                    city_id: JSON.stringify(params.data.cityId ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    deal_done_by_id: params.data.deal_done_by_id?.id ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    age_op: params.data.age_op ?? "",
                    deal_type: params.data.dealType ?? "",
                    raised_on_end: params.data.TicketEndDate ?? "",
                    raised_on_start: params.data.TicketStartDate ?? "",
                    updated_on_start: params.data.UpdateStartDate ?? "",
                    updated_on_end: params.data.UpdateEndDate ?? "",
                    charger_type: JSON.stringify(params.data.chargerIds ?? []),
                    vendor_ids: JSON.stringify(params.data.vendor_ids ?? []),
                    search_key: params.data.search_key ?? "",
                    raised_by_id: params.data.raised_by_id?.id ?? "",
                    blocked_on: JSON.stringify(params.data.blocked_on ?? []),
                    read_filter: params.data.ReadUnread ?? "all",
                    per_page: params?.selectedValue,
                    page: params?.page,
                    site_ids: JSON.stringify(params.data.siteIds ?? []),
                    blocked_reason_id: JSON.stringify(params.data.blocked_reason_id ?? []),
                },
            }),
            transformResponse: (response: { message: StatusTabsData[] }) => response.message,
            providesTags: ["Installation"],
        }),
        getInstallationData: build.query<TicketDataResponseType, { data: any; selectedValue: number; page: number }>({
            query: params => ({
                url: `${MODULE_URL}/ticket/${params.data.parent_status ?? "all_tab"}`,
                params: {
                    state_id: JSON.stringify(params.data.stateId ?? []),
                    status_id: JSON.stringify(params.data.statusId ?? []),
                    city_id: JSON.stringify(params.data.cityId ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    deal_done_by_id: params.data.deal_done_by_id?.id ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    deal_type: params.data.dealType ?? "",
                    raised_on_end: params.data.TicketEndDate ?? "",
                    raised_on_start: params.data.TicketStartDate ?? "",
                    updated_on_start: params.data.UpdateStartDate ?? "",
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    updated_on_end: params.data.UpdateEndDate ?? "",
                    charger_type: JSON.stringify(params.data.chargerIds ?? []),
                    vendor_ids: JSON.stringify(params.data.vendor_ids ?? []),
                    search_key: params.data.search_key ?? "",
                    raised_by_id: params.data.raised_by_id?.id ?? "",
                    blocked_on: JSON.stringify(params.data.blocked_on ?? []),
                    read_filter: params.data.ReadUnread ?? "all",
                    per_page: params?.selectedValue,
                    page: params?.page,
                    site_ids: JSON.stringify(params.data.siteIds ?? []),
                    blocked_reason_id: JSON.stringify(params.data.blocked_reason_id ?? []),
                },
            }),
            providesTags: ["Installation"],
        }),
        getRaisedByUsers: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `${MODULE_URL}/raised_by_users`,
            }),
            providesTags: ["Installation"],
        }),
        getInstallationDealDetails: build.query<any, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/ops_ticket_detail`,
                params: {
                    ticket_id,
                },
            }),
            providesTags: (result, error, arg) => ["Installation", { type: "Installation", id: arg.ticket_id }],
        }),
        getInstallationOtherDetails: build.query<
            {
                cards: CardData[]
                images: { images: any[] }
                delivery_request: any
                reverse_delivery_request: any
                project_id: any
            },
            { ticket_id: string }
        >({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/other_details`,
                params: {
                    ticket_id,
                },
            }),
            providesTags: (result, error, arg) => ["Installation", { type: "Installation", id: arg.ticket_id }],
        }),
        getOpsButtonDropdown: build.query<StatusButton[], { ticket_id: string; sub_module: string; role_id: string }>({
            query: params => ({
                url: `${MODULE_URL}/button_dropdown`,
                params,
            }),
            providesTags: ["Installation", "EvApplication"],
        }),
        getOpsCompletionDate: build.query<
            OpsCompletionDateObj[],
            { ticket_id: string; module_id: number; submodule_id: number }
        >({
            query: params => ({
                url: `${MODULE_URL}/completion_date`,
                params,
            }),
            providesTags: ["Installation", "EvApplication"],
        }),
        getOpsCost: build.query<any, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/costs`,
                params: {
                    ticket_id,
                },
            }),
            providesTags: (result, error, arg) => [{ type: "Installation", id: arg.ticket_id }],
        }),
        // For dochub
        getDocHubCategories: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `dochub_category`,
            }),
            transformResponse: (response: IResponse<{ id: number; name: string }[]>) => response.data,
        }),
        getOpsTicketStage: build.query<{ id: number; status: string }[], void>({
            query: () => ({
                url: `${MODULE_URL}/ticket_stage`,
            }),
            transformResponse: (response: IResponse<{ id: number; status: string }[]>) => response.data,
        }),
        getDochubData: build.query<DocHubObj[], { ticket_id: number; ticket_stage: string; doc_type: string }>({
            query: params => ({
                url: `${MODULE_URL}/dochub`,
                params,
            }),
            transformResponse: (response: IResponse<DocHubObj[]>) => response.data,
            providesTags: ["Installation"],
        }),
        getLinkSurveyList: build.query<IResponse<{ id: number; name: string }[]>, void>({
            query: () => ({
                url: `${MODULE_URL}/survey/link_survey`,
            }),
            providesTags: (result, error, arg) => ["Installation"],
        }),
        downloadInstallationTickets: build.query<any, { data: any; selectedValue: number; page: number }>({
            query: params => ({
                url: `${MODULE_URL}/list_download/${params.data.parent_status ?? "all_tab"}`,
                params: {
                    state_id: JSON.stringify(params.data.stateId ?? []),
                    status_id: JSON.stringify(params.data.statusId ?? []),
                    city_id: JSON.stringify(params.data.cityId ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    deal_done_by_id: params.data.deal_done_by_id?.id ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    deal_type: params.data.dealType ?? "",
                    raised_on_end: params.data.TicketEndDate ?? "",
                    raised_on_start: params.data.TicketStartDate ?? "",
                    updated_on_start: params.data.UpdateStartDate ?? "",
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    updated_on_end: params.data.UpdateEndDate ?? "",
                    charger_type: JSON.stringify(params.data.chargerIds ?? []),
                    vendor_ids: JSON.stringify(params.data.vendor_ids ?? []),
                    search_key: params.data.search_key ?? "",
                    raised_by_id: params.data.raised_by_id?.id ?? "",
                    blocked_on: JSON.stringify(params.data.blocked_on ?? []),
                    read_filter: params.data.ReadUnread ?? "all",
                    per_page: params?.selectedValue,
                    page: params?.page,
                    site_ids: JSON.stringify(params.data.siteIds ?? []),
                },
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.link ? "Data Downloaded" : data?.msg }))
                } catch (error: any) {}
            },
            keepUnusedDataFor: 0,
        }),
        // Mutations
        addDocHubData: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/dochub`,
                method: "POST",
                body,
            }),
            invalidatesTags: ["Installation"],
        }),

        addSiteSurveyCompletionDate: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/delivery_date`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                    if (arg.get("source") === "projects") {
                        dispatch(getTicketDetails(arg.get("project_id") as string))
                    }
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Installation", id: arg.get("ticket_id") as string },
                "Installation",
                "Projects",
            ],
        }),

        siteReadyForDispatch: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/site_ready_for_dispatch`,
                method: "PUT",
                body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Installation", id: arg.get("ticket_id") as string },
                "Installation",
            ],
        }),

        requestSiteSurvey: build.mutation<IResponse<[string]>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/survey/request_survey`,
                method: "POST",
                body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data.data[0] }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Installation", id: arg.get("ops_ticket_id") as string },
                "Installation",
                "Projects",
            ],
        }),

        editOpsCompletionDate: build.mutation<{ msg: string }, FormData>({
            query: body => ({
                url: `${MODULE_URL}/completion_date`,
                method: "PUT",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    const state: any = getState()
                    const { DemolitionFormValue, page: demoPage, selectedValue: demoSelectedValue } = state.operation
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                    arg.get("submodule_id") === "21" &&
                        dispatch(
                            getDemolitionTabs({
                                data: DemolitionFormValue,
                                page: demoPage,
                                selectedValue: demoSelectedValue,
                            })
                        )
                    arg.get("submodule_id") === "21" &&
                        dispatch(
                            getDemolition({
                                data: DemolitionFormValue,
                                page: demoPage,
                                selectedValue: demoSelectedValue,
                            })
                        )
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Installation", id: arg.get("ticket_id") as string },
                "Installation",
                "EvApplication",
            ],
        }),

        editOpsTicketDetails: build.mutation<{ message: string }, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ticket`,
                method: "PUT",
                body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Installation", id: arg.get("ticket_id") as string },
                "Installation",
            ],
        }),

        addDRDetails: build.mutation<any, FormData>({
            query: body => ({
                url: `${MODULE_URL}/upload_dr_v2`,
                method: "POST",
                body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: typeof data?.data === "string" ? data.data : data.message,
                        })
                    )
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.error?.data?.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Installation", id: arg.get("ticket_id") as string },
                "Installation",
            ],
        }),
        updateRMCost: build.mutation<IResponse<string>, { ticket_id: number | string; rm_cost: number }>({
            query: body => ({
                url: `${MODULE_URL}/ops_ticket_detail`,
                method: "PUT",
                body: body,
            }),
            invalidatesTags: (result, error, args) => [
                { type: "Installation", id: args.ticket_id as string },
                "Installation",
            ],
        }),
        editOpsVendor: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/edit_vendor`,
                method: "PUT",
                body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: "Vendor updated successfully" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, args) => [
                { type: "Installation", id: args.get("ticket_id") as string },
                "Installation",
            ],
        }),

        editLinkSiteSurvey: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/survey/link_survey`,
                method: "PUT",
                body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Installation", id: arg.get("ticket_id") as string },
                "Installation",
            ],
        }),

        // ==================== V3 Endpoints ====================
        getInstallationListTabs: build.query<IInstallationTabCount[], IInstallationListFilters>({
            query: (filters = {}) => ({
                url: `${MODULE_URL}/list/total`,
                params: filters,
                useV3: true,
            }),
            transformResponse: (res: { message: IInstallationTabCount[] }) => res.message,
            providesTags: ["Installation"],
        }),

        getInstallationList: build.query<
            IInstallationListResponse,
            { parent_status: InstallationParentStatus } & IInstallationListFilters
        >({
            query: ({ parent_status, ...filters }) => ({
                url: `${MODULE_URL}/list/${parent_status}`,
                params: filters,
                useV3: true,
            }),
            providesTags: ["Installation"],
        }),

        downloadInstallationList: build.query<
            IFile,
            { parent_status: InstallationParentStatus } & IInstallationListFilters
        >({
            query: filters => ({
                url: `${MODULE_URL}/list/download`,
                params: filters,
                useV3: true,
            }),
            providesTags: ["Installation"],
        }),

        getInstallationStats: build.query<IInstallationStatsResponse, string>({
            query: ticketId => ({
                url: `${MODULE_URL}/${ticketId}/overall-progress`,
                useV3: true,
            }),
            transformResponse: (response: {
                data: IInstallationStatsResponse
                success: boolean
                project_id: number
            }) => {
                return response.data
            },
            providesTags: ["Projects", "Installation"],
        }),

        getInstallationOverview: build.query<IProjectProfileOverviewResponse, string>({
            query: projectId => ({
                url: `${PROJECT_URL}/project_profile_overview`,
                params: { project_id: projectId },
                useV3: true,
            }),
            transformResponse: (response: {
                data: IProjectProfileOverviewResponse
                success: boolean
                project_id: number
            }) => {
                return response.data
            },
            providesTags: ["Projects", "Installation"],
        }),

        getInstallationHistory: build.query<HistoryResponse, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/history`,
                useV3: true,
            }),
            providesTags: (result, error, arg) => ["Installation"],
        }),
        markInstallationTicketasRead: build.mutation<IResponse<string>, { ops_ticket_id: number | string }>({
            query: body => ({
                url: `${MODULE_URL}/mark_read`,
                useV3: true,
                method: "POST",
                body,
            }),
            invalidatesTags: ["Installation"],
        }),

        getInstallationTicketEstimates: build.query<
            IEstimateResponse,
            { ticket_id: number | string; project_id: number | string }
        >({
            query: ({ ticket_id, project_id }) => ({
                url: `${MODULE_URL}/estimate`,
                params: {
                    ticket_id,
                    project_id,
                },
                useV3: true,
            }),
            providesTags: ["Installation"],
        }),

        getGrnDetails: build.query<IGrnDetailsResponse, { item_id: number; boq_id: number }>({
            query: params => ({
                url: `${MODULE_URL}/grn`,
                params,
                useV3: true,
            }),
            providesTags: ["Installation"],
        }),

        uploadGrn: build.mutation<IUploadGrnResponse, FormData>({
            query: body => ({
                url: `${MODULE_URL}/grn`,
                method: "POST",
                body,
                useV3: true,
            }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "GRN uploaded successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || error?.message || "Failed to upload GRN", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Installation"],
        }),

        getBoqGrnDetails: build.query<IBoqGrnDetailsResponse, { boq_id: number; item_id: number }>({
            query: params => ({
                url: `${MODULE_URL}/boq-grn`,
                params,
                useV3: true,
            }),
            providesTags: ["Installation"],
        }),

        addBoqGrn: build.mutation<IAddBoqGrnResponse, FormData>({
            query: body => ({
                url: `${MODULE_URL}/boq-grn`,
                method: "POST",
                body,
                useV3: true,
            }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "GRN added successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || error?.message || "Failed to add GRN", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Installation"],
        }),

        addBulkBoqGrn: build.mutation<IBulkAddBoqGrnResponse, { boq_id: number; items: BulkGrnSubmitItem[] }>({
            query: body => ({
                url: `${MODULE_URL}/boq-grn/bulk`,
                method: "POST",
                body,
                useV3: true,
            }),
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(`Bulk GRN added (${data.added_count})`, { variant: "success" })
                    data.errors?.forEach(err => {
                        enqueueSnackbar(`Item ${err.item_id}: ${err.reason}`, { variant: "warning" })
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || error?.message || "Failed to add bulk GRN", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Installation"],
        }),

        getInstallationButtonDropdown: build.query<StatusButton[], string | number>({
            query: ticketId => ({
                url: `${MODULE_URL}/${ticketId}/button_dropdown`,
                useV3: true,
            }),
            providesTags: ["Installation"],
            transformResponse: (response: { buttons: StatusButton[] }) => response.buttons,
        }),

        getInstallationOnHoldTeams: build.query<string[], void>({
            query: () => ({
                url: `${MODULE_URL}/on_hold_teams`,
                useV3: true,
            }),
            transformResponse: (res: { data: string[] }) => res.data,
        }),

        getAllOpsContractors: build.query<
            SurveyContractor[],
            {
                ticketId: number | string
            }
        >({
            query: ({ ticketId }) => ({
                url: `${MODULE_URL}/${ticketId}/contractors`,
                useV3: true,
            }),
            providesTags: ["Installation"],
        }),

        getOpsReasons: build.query<
            { id: number; reason: string }[],
            {
                reasonType?: "drop" | "on_hold" | "survey_waiver"
            }
        >({
            query: ({ reasonType = "on_hold" }) => ({
                url: `${MODULE_URL}/reasons`,
                params: {
                    type: reasonType,
                },
                useV3: true,
            }),
            transformResponse: (res: { data: { id: number; reason: string }[] }) => res.data,
            providesTags: ["Installation"],
        }),

        updateOpsOnHoldStatus: build.mutation<
            { message: string },
            {
                on_hold: boolean
                project_id: number
                reason_id: number
                standby: string
                tentative_date: string
                ticketId: string
            }
        >({
            query: ({ ticketId, ...body }) => ({
                url: `${MODULE_URL}/${ticketId}/on_hold`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Installation"],
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || error?.message || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
        }),

        addOpsContractors: build.mutation<
            { message: string },
            {
                vendor_id: number
                executive_id: number
                ticketId: number | string
                sow_group_ids: number[]
                deactivate_existing?: boolean
            }
        >({
            query: ({ ticketId, ...body }) => ({
                url: `${MODULE_URL}/${ticketId}/contractors`,
                method: "POST",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Installation"],
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Contractor added successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || error?.message || "Failed to add contractor", {
                        variant: "error",
                    })
                }
            },
        }),
        editOpsContractor: build.mutation<
            { message: string },
            {
                ticketId: number | string
                vts_id?: number
                is_active?: boolean
                executive_id?: number
                rebuild_vendor_code?: boolean
                sow_group_ids?: number[]
                deactivate_existing?: boolean
            }
        >({
            query: ({ ticketId, ...body }) => ({
                url: `${MODULE_URL}/${ticketId}/contractors`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Installation"],
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Contractor updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || error?.message || "Failed to update contractor", {
                        variant: "error",
                    })
                }
            },
        }),

        editInstallationTicketStatus: build.mutation<
            { new_status: string; message: string },
            IInstallationStatusUpdatePayload | FormData
        >({
            query: body => {
                let ticketId: string | number | undefined
                let payload: IInstallationStatusUpdatePayload | FormData

                if (body instanceof FormData) {
                    ticketId = body.get("ticket_id") as string
                    body.delete("ticket_id")
                    payload = body
                } else {
                    const { ticket_id, ...rest } = body
                    ticketId = ticket_id
                    payload = rest as IInstallationStatusUpdatePayload
                }

                return {
                    url: `${MODULE_URL}/${ticketId}/ticket`,
                    method: "PUT",
                    useV3: true,
                    body: payload,
                }
            },
            // TaskStrip: a status move can close or unblock stages, so the strip re-reads.
            invalidatesTags: ["Installation", "Projects", "TaskStrip"],
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Ticket status updated!", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || error?.message || "Failed to update status", {
                        variant: "error",
                    })
                }
            },
        }),
        requestNewSurvey: build.mutation<
            {
                message: string
                survey_id: number
                display_id: string
                project_id: number
            },
            {
                visit_by_date: string
                time_slot: number
                ticketId: string | number
            }
        >({
            query: ({ ticketId, ...body }) => ({
                url: `${MODULE_URL}/${ticketId}/request_survey`,
                method: "POST",
                useV3: true,
                body,
            }),
            // TaskStrip: a new survey is what the survey stage follows.
            invalidatesTags: ["Survey", "Projects", "Installation", "TaskStrip"],
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Ticket Created!", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || error?.message || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
        }),

        getInstallationChargerDetails: build.query<
            IChargerDetailsResponse,
            {
                ticketId: string | number
                view: "detailed" | "sku"
                operational_status?: string
                onb_ids?: boolean
            }
        >({
            query: ({ view = "detailed", ticketId, operational_status, onb_ids }) => ({
                url: `${MODULE_URL}/${ticketId}/charger_details`,
                params: {
                    view,
                    ...(operational_status ? { operational_status } : {}),
                    ...(onb_ids ? { onb_ids: true } : {}),
                },
                useV3: true,
            }),
            transformResponse: (response: { data: IChargerDetailsResponse }) => response.data,
            providesTags: ["Installation", "Projects", "Onboarding"],
        }),

        getChargerPresignedUpload: build.mutation<IChargerPresignedUploadResponse, IChargerPresignedUploadRequest>({
            query: body => ({
                url: `chargers/presigned_upload`,
                method: "POST",
                body,
                useV3: true,
            }),
        }),

        bulkUpdateChargerStatus: build.mutation<{ message: string }, IBulkUpdateChargerStatusRequest>({
            query: body => ({
                url: `chargers/bulk_update_status`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Installation", "Projects"],
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Charger status updated!", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || error?.message || "Failed to update status", {
                        variant: "error",
                    })
                }
            },
        }),

        getLinkableEVATickets: build.query<
            {
                projectId: number
                linked_appli_ticket_id: number | null
                solo_appli_tickets: IAppiTicket[]
            },
            {
                ticketId: string | number
            }
        >({
            query: ({ ticketId }) => ({
                url: `${MODULE_URL}/${ticketId}/appli_ticket`,
                useV3: true,
            }),
            providesTags: ["Installation", "EvApplication"],
        }),
        linkEvApplication: build.mutation<
            { message: string; project_id: number },
            {
                evaTicketId: number
                ticketId: string | number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/${body.ticketId}/link_appli_ticket`,
                method: "PUT",
                useV3: true,
                body: {
                    appli_ticket_id: body.evaTicketId,
                },
            }),
            invalidatesTags: ["EvApplication", "Installation", "Projects"],
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "EV Application linked successfully!", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.message || "Something went wrong!", { variant: "error" })
                }
            },
        }),

        getInstallationTicketActivity: build.query<
            {
                tickets: IProjectTicket[]
                msrd_enabled: boolean
                total: number
                project_id: number
            },
            { ticketId: number | string }
        >({
            query: ({ ticketId }) => ({
                url: `${MODULE_URL}/${ticketId}/ticket-details`,
                useV3: true,
            }),
            providesTags: ["Installation", "Projects"],
        }),

        requestNewEvApplication: build.mutation<
            { message: string },
            {
                discom_id: number
                start_date: string
                ticketId: string | number
            }
        >({
            query: ({ ticketId, ...body }) => ({
                url: `${MODULE_URL}/${ticketId}/create_appli_ticket`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: ["EvApplication", "Installation", "Projects"],
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "EV Application created!", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || error?.message || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
        }),

        getTicketApprovals: build.query<
            {
                data: {
                    id: number
                    name: string
                    type: string
                    status: string
                    raised_by: string
                    raised_on: string
                    remarks: string
                    approvers: { name: string; action: string; reason: string | null; sequence: number }[]
                    entities: {
                        boq_id: number
                        boq_name: string
                        description: string
                        quantity: number
                        sku_code: string
                        source: string
                        status: string
                        total_cost: number
                    }[]
                }[]
            },
            { ticket_id: string | number; module_id: number; submodule_id: number }
        >({
            query: params => ({
                url: `approvals/ticket_approvals`,
                params,
                useV3: true,
            }),
            providesTags: ["Installation"],
        }),

        getBoqApprovers: build.query<
            { manager: { id: number; name: string } | null; users: { id: number; name: string }[] },
            { user_id: number | string }
        >({
            query: ({ user_id }) => ({
                url: `approvals/approvers`,
                params: { user_id },
                useV3: true,
            }),
        }),

        getBoqApprovalBoqs: build.query<
            { data: { cost: string; id: number; name: string; vendor_id: number | null }[] },
            { approval_type: string; project_id: string; module_id: number; ticket_id: string; submodule_id: number }
        >({
            query: params => ({
                url: `approvals/boqs`,
                params,
                useV3: true,
            }),
            /**
             * Without this the list never refreshes. createBoqApproval invalidates
             * "Installation", but an untagged query is not subscribed to anything, so a BOQ
             * approved a moment ago stayed missing from the Project Estimate form until a
             * full page reload.
             */
            providesTags: ["Installation"],
        }),

        createBoqApproval: build.mutation<{ message: string }, FormData>({
            query: body => ({
                url: `approvals/create`,
                method: "POST",
                body,
                useV3: true,
            }),
            // TaskStrip: raising the approval moves the BOQ approval stage.
            invalidatesTags: ["Installation", "TaskStrip"],
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Approval submitted successfully!", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || error?.message || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
        }),
//change onboarding initate correct endpoint once provided
        initiateOnboarding: build.mutation<
            { message: string; ticket_id?: number },
            ICreateOnboardingTicketPayload | FormData
        >({
            query: body => ({
                url: `onboarding/ticket`,
                method: "POST",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Installation", "Projects", "Onboarding"],
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Onboarding initiated successfully!", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || error?.message || "Failed to initiate onboarding", {
                        variant: "error",
                    })
                }
            },
        }),
    }),
})

export const {
    // V3 hooks
    useGetInstallationListTabsQuery,
    useGetInstallationListQuery,
    useGetInstallationStatsQuery,
    useGetInstallationOverviewQuery,
    useMarkInstallationTicketasReadMutation,
    useGetInstallationTicketEstimatesQuery,
    useGetInstallationButtonDropdownQuery,
    useGetGrnDetailsQuery,
    useLazyGetGrnDetailsQuery,
    useUploadGrnMutation,
    useLazyGetBoqGrnDetailsQuery,
    useAddBoqGrnMutation,
    useAddBulkBoqGrnMutation,
    useGetAllOpsContractorsQuery,
    useAddOpsContractorsMutation,
    useEditOpsContractorMutation,
    useGetOpsReasonsQuery,
    useUpdateOpsOnHoldStatusMutation,
    useGetInstallationOnHoldTeamsQuery,
    useEditInstallationTicketStatusMutation,
    useRequestNewSurveyMutation,
    useLazyDownloadInstallationListQuery,
    useGetInstallationChargerDetailsQuery,
    useGetChargerPresignedUploadMutation,
    useBulkUpdateChargerStatusMutation,
    useGetLinkableEVATicketsQuery,
    useLinkEvApplicationMutation,
    useRequestNewEvApplicationMutation,
    useGetInstallationTicketActivityQuery,
    useGetTicketApprovalsQuery,
    useGetBoqApproversQuery,
    useGetBoqApprovalBoqsQuery,
    useCreateBoqApprovalMutation,
    useInitiateOnboardingMutation,

    // Legacy hooks
    useGetInstallationTabsQuery,
    useGetInstallationDataQuery,
    useLazyGetInstallationTabsQuery,
    useLazyGetInstallationDataQuery,
    useGetRaisedByUsersQuery,
    useGetInstallationDealDetailsQuery,
    useGetInstallationOtherDetailsQuery,
    useGetOpsButtonDropdownQuery,
    useGetOpsCompletionDateQuery,
    useGetOpsCostQuery,
    useGetDocHubCategoriesQuery,
    useGetOpsTicketStageQuery,
    useGetDochubDataQuery,
    useGetLinkSurveyListQuery,
    useGetInstallationHistoryQuery,
    useLazyDownloadInstallationTicketsQuery,

    // Mutations
    useAddDocHubDataMutation,
    useAddSiteSurveyCompletionDateMutation,
    useSiteReadyForDispatchMutation,
    useEditOpsCompletionDateMutation,
    useEditOpsTicketDetailsMutation,
    useEditLinkSiteSurveyMutation,
    useRequestSiteSurveyMutation,
    useAddDRDetailsMutation,
    useUpdateRMCostMutation,
    useEditOpsVendorMutation,
} = installationService

export { installationService }
